import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import CloudUploadOutlined from "@mui/icons-material/CloudUploadOutlined";

import Upload from "../../../../Upload";
import {
  UIButton,
  UIModal,
  UISelect,
  UITextField,
  UITextarea,
} from "../../../../ui";
import TrashIcon from "../../../../ui/SVG/TrashIcon";
import { clx } from "../../../../../utils/Helpers";
import {
  getReportAttachmentSlotsRemaining,
  getReportUploadHint,
  isReportImageFile,
  REPORT_DESCRIPTION_MAX_LENGTH,
  REPORT_IMAGE_ACCEPT,
  REPORT_MAX_ATTACHMENTS,
  uploadReportAttachments,
} from "../reportUpload";

import "./AddReportModal.scss";

const INITIAL_VALUES = {
  title: "",
  event: null,
  zone: null,
  description: "",
  status: "open",
  files: [],
  previews: [],
};

const AddReportModal = ({
  open,
  labels,
  zoneOptions,
  eventOptions,
  showEventSelect,
  submitting,
  onClose,
  onSubmit,
  onEventChange,
}) => {
  const [values, setValues] = useState(INITIAL_VALUES);
  const [touched, setTouched] = useState({});
  const [uploadPercent, setUploadPercent] = useState(null);
  const [formLocked, setFormLocked] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);

  useEffect(() => {
    if (!open) {
      setValues(INITIAL_VALUES);
      setTouched({});
      setUploadPercent(null);
      setFormLocked(false);
      setUploadKey(0);
    }
  }, [open]);

  useEffect(() => {
    if (!showEventSelect) return;
    setValues((prev) => ({ ...prev, zone: null }));
  }, [values.event?.id, showEventSelect]);

  const descriptionLength = String(values.description || "").length;
  const descriptionTooLong = descriptionLength > REPORT_DESCRIPTION_MAX_LENGTH;

  const errors = useMemo(() => {
    const next = {};
    if (!String(values.title || "").trim()) next.title = true;
    if (showEventSelect && !values.event?.id) next.event = true;
    if (!values.zone?.id) next.zone = true;
    if (!String(values.description || "").trim()) next.description = true;
    if (descriptionTooLong) next.descriptionLength = true;
    return next;
  }, [values, showEventSelect, descriptionTooLong]);

  const isUploading = uploadPercent !== null;
  const busy = submitting || isUploading || formLocked;
  const canSubmit = Object.keys(errors).length === 0 && !busy;
  const slotsRemaining = getReportAttachmentSlotsRemaining(values.files.length);
  const atAttachmentLimit = slotsRemaining <= 0;
  const { text: attachmentHint, formatText: attachmentFormatHint, tone: attachmentHintTone } =
    getReportUploadHint(labels, slotsRemaining);

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const handleConfirm = async () => {
    setTouched({
      title: true,
      event: true,
      zone: true,
      description: true,
    });
    if (Object.keys(errors).length > 0 || busy) return;

    setFormLocked(true);
    let imageUrls = [];
    try {
      if (values.files.length) {
        setUploadPercent(0);
        imageUrls = await uploadReportAttachments(values.files, {
          onProgress: (percent) => setUploadPercent(percent),
        });
        setUploadPercent(100);
      }
      onSubmit({
        title: String(values.title).trim(),
        description: String(values.description).trim(),
        status: values.status,
        eventId: values.event?.id,
        zoneId: values.zone?.id,
        files: values.files,
        imageUrls,
        imageUrl: imageUrls[0] || null,
      });
    } catch (error) {
      setUploadPercent(null);
      setFormLocked(false);
    }
  };

  const handleAttachmentPick = (preview, file) => {
    if (!file || !isReportImageFile(file)) {
      setUploadKey((key) => key + 1);
      return;
    }

    setValues((prev) => {
      if (prev.files.length >= REPORT_MAX_ATTACHMENTS) return prev;
      const nextPreview =
        typeof preview === "string" && preview
          ? preview
          : URL.createObjectURL(file);
      return {
        ...prev,
        files: [...prev.files, file],
        previews: [...prev.previews, nextPreview],
      };
    });
    setUploadKey((key) => key + 1);
  };

  const handleRemoveAttachment = (index) => {
    setValues((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
      previews: prev.previews.filter((_, i) => i !== index),
    }));
  };

  const descriptionHelper = (() => {
    if (touched.description && errors.description) return labels.required;
    if (touched.description && errors.descriptionLength) {
      return labels.descriptionMaxLengthError;
    }
    return labels.descriptionHint;
  })();

  const uploadArea = (
    <div
      className={clx(
        "add-report-modal__dropzone",
        atAttachmentLimit && "add-report-modal__dropzone--disabled",
      )}
    >
      <div className="add-report-modal__dropzone-icon-wrap">
        <CloudUploadOutlined className="add-report-modal__dropzone-icon" />
      </div>
      <p className="add-report-modal__dropzone-line mb-0 text-center">
        <span className="add-report-modal__dropzone-action">
          {labels.uploadClickToUpload}
        </span>
        <span className="add-report-modal__dropzone-rest">
          {" "}
          {labels.uploadDragHint}
        </span>
      </p>
      <p className="add-report-modal__dropzone-hint mb-0 text-center">
        {attachmentHintTone === "remaining" ? (
          <>
            <span className="add-report-modal__dropzone-hint--warning">
              {attachmentHint}
            </span>
            {attachmentFormatHint ? ` ${attachmentFormatHint}` : null}
          </>
        ) : attachmentHintTone === "limit" ? (
          <span className="add-report-modal__dropzone-hint--warning">
            {attachmentHint}
          </span>
        ) : (
          attachmentHint
        )}
      </p>
    </div>
  );

  return (
    <UIModal
      open={open}
      type="info"
      title={labels.title}
      description={labels.description}
      cancelLabel={labels.cancel}
      confirmLabel={labels.submit}
      onClose={handleClose}
      onCancel={handleClose}
      onConfirm={handleConfirm}
      cancelButtonProps={{
        disabled: busy,
      }}
      confirmButtonProps={{
        disabled: !canSubmit,
        appearance: "gradient",
        loading: busy,
      }}
      PaperProps={{ className: "add-report-modal" }}
      maxWidth="sm"
      fullWidth
    >
      <div className="add-report-modal__fields" aria-busy={busy}>
        <UITextField
          name="reportTitle"
          label={labels.reportTitle}
          placeholder={labels.reportTitlePlaceholder}
          value={values.title}
          required
          disabled={busy}
          error={Boolean(touched.title && errors.title)}
          helperText={
            touched.title && errors.title ? labels.required : undefined
          }
          onChange={(event) =>
            setValues((prev) => ({ ...prev, title: event.target.value }))
          }
          onBlur={() => setTouched((prev) => ({ ...prev, title: true }))}
        />

        {showEventSelect ? (
          <UISelect
            name="reportEvent"
            label={labels.event}
            placeholder={labels.eventPlaceholder}
            options={eventOptions}
            value={values.event}
            required
            disabled={busy}
            error={Boolean(touched.event && errors.event)}
            helperText={
              touched.event && errors.event ? labels.required : undefined
            }
            onChange={(event) => {
              setValues((prev) => ({ ...prev, event, zone: null }));
              onEventChange?.(event?.id ?? null);
            }}
            onBlur={() => setTouched((prev) => ({ ...prev, event: true }))}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, selected) =>
              option?.id === selected?.id
            }
          />
        ) : null}

        <UISelect
          name="reportZone"
          label={labels.zone}
          placeholder={labels.zonePlaceholder}
          options={zoneOptions}
          value={values.zone}
          required
          disabled={busy || (showEventSelect && !values.event?.id)}
          error={Boolean(touched.zone && errors.zone)}
          helperText={
            touched.zone && errors.zone ? labels.required : undefined
          }
          onChange={(zone) => setValues((prev) => ({ ...prev, zone }))}
          onBlur={() => setTouched((prev) => ({ ...prev, zone: true }))}
          getOptionLabel={(option) => option?.name || ""}
          isOptionEqualToValue={(option, selected) =>
            option?.id === selected?.id
          }
        />

        <UITextarea
          name="reportDescription"
          label={labels.issueDescription}
          placeholder={labels.issueDescriptionPlaceholder}
          value={values.description}
          required
          disabled={busy}
          minRows={2.5}
          maxLength={REPORT_DESCRIPTION_MAX_LENGTH}
          maxLengthMessage={labels.descriptionMaxLengthError}
          error={Boolean(
            touched.description &&
              (errors.description || errors.descriptionLength),
          )}
          helperText={descriptionHelper}
          onValueChange={(description) =>
            setValues((prev) => ({
              ...prev,
              description: String(description || ""),
            }))
          }
          onBlur={() => setTouched((prev) => ({ ...prev, description: true }))}
        />

        <div className="add-report-modal__status-field ui-field">
          <div className="ui-field__header">
            <Typography className="ui-field__label" component="span">
              {labels.reportStatus}
              <span className="ui-field__required">*</span>
            </Typography>
          </div>
          <Box className="add-report-modal__status ui-field__control">
            <UIButton
              type="button"
              variant="outline"
              size="medium"
              disabled={busy}
              className={clx(
                "add-report-modal__status-btn",
                values.status === "open" &&
                  "add-report-modal__status-btn--active",
              )}
              onClick={() =>
                setValues((prev) => ({ ...prev, status: "open" }))
              }
            >
              {labels.open}
            </UIButton>
            <UIButton
              type="button"
              variant="outline"
              size="medium"
              disabled={busy}
              className={clx(
                "add-report-modal__status-btn",
                values.status === "closed" &&
                  "add-report-modal__status-btn--active",
              )}
              onClick={() =>
                setValues((prev) => ({ ...prev, status: "closed" }))
              }
            >
              {labels.closed}
            </UIButton>
          </Box>
        </div>

        <div className="add-report-modal__upload-field ui-field">
          <div className="ui-field__header">
            <Typography className="ui-field__label" component="span">
              {labels.uploadLabel}
            </Typography>
          </div>
          <div className="add-report-modal__upload-field-body ui-field__control">
            <Upload
              key={uploadKey}
              id="add-report-attachment"
              name="reportAttachment"
              className="add-report-modal__upload w-100"
              wrapperClasses="w-100"
              isImage
              accept={REPORT_IMAGE_ACCEPT}
              uploadArea={uploadArea}
              img=""
              maxFileSizeBytes={10 * 1024 * 1024}
              invalidSizeMessage={labels.uploadMaxSizeError}
              invalidTypeMessage={labels.uploadTypeError}
              isUploading={isUploading}
              uploadProgress={isUploading ? uploadPercent : undefined}
              uploadProgressLabel={
                isUploading ? `${uploadPercent}%` : undefined
              }
              onChange={handleAttachmentPick}
              disabled={busy || atAttachmentLimit}
            />

            {values.files.length > 0 ? (
              <div className="add-report-modal__attachments">
                {values.files.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="add-report-modal__attachment"
                  >
                    <img
                      className="add-report-modal__attachment-preview"
                      src={values.previews[index]}
                      alt=""
                    />
                    <span className="add-report-modal__attachment-name">
                      {file.name}
                    </span>
                    {!busy ? (
                      <button
                        type="button"
                        className="add-report-modal__attachment-remove"
                        aria-label={labels.removeAttachment || "Remove file"}
                        onClick={() => handleRemoveAttachment(index)}
                      >
                        <TrashIcon
                          width={20}
                          height={20}
                          color="rgba(102, 102, 102, 1)"
                        />
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </UIModal>
  );
};

AddReportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  labels: PropTypes.shape({
    title: PropTypes.node.isRequired,
    description: PropTypes.node,
    cancel: PropTypes.node.isRequired,
    submit: PropTypes.node.isRequired,
    reportTitle: PropTypes.node.isRequired,
    reportTitlePlaceholder: PropTypes.string,
    event: PropTypes.node,
    eventPlaceholder: PropTypes.string,
    zone: PropTypes.node.isRequired,
    zonePlaceholder: PropTypes.string,
    issueDescription: PropTypes.node.isRequired,
    issueDescriptionPlaceholder: PropTypes.string,
    descriptionHint: PropTypes.node,
    descriptionMaxLengthError: PropTypes.node,
    open: PropTypes.node.isRequired,
    closed: PropTypes.node.isRequired,
    reportStatus: PropTypes.node,
    uploadLabel: PropTypes.node,
    uploadClickToUpload: PropTypes.node,
    uploadDragHint: PropTypes.node,
    uploadHint: PropTypes.node,
    uploadFormatHint: PropTypes.node,
    uploadRemainingHint: PropTypes.node,
    uploadLimitReached: PropTypes.node,
    uploadMaxSizeError: PropTypes.string,
    uploadTypeError: PropTypes.string,
    removeAttachment: PropTypes.node,
    required: PropTypes.node,
  }).isRequired,
  zoneOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  eventOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  showEventSelect: PropTypes.bool,
  submitting: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onEventChange: PropTypes.func,
};

AddReportModal.defaultProps = {
  zoneOptions: [],
  eventOptions: [],
  showEventSelect: false,
  submitting: false,
  onEventChange: undefined,
};

export default AddReportModal;
